<template>
    <div class="dochub-viewer">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div v-if="!hideTitle" class="w-100 d-flex justify-content-between align-items-start mb-5">
            <div class="mb-0">
                <h3 class="mb-0">Supplier Document Hub</h3>
            </div>
        </div>
        
        <!-- <div class="d-flex flex-wrap"> -->
        <div v-if="!initialLoadComplete || loading" key="loading" class="w-100" style="min-height: 400px; height: 400px;">
            <busy-spinner text="Loading..." class="h-100 w-100"></busy-spinner>
        </div>
        <div
            v-else 
            key="notLoading"
            class=""
        >
            <div class="row">
                <div id="dochub-groups-content" class="col" style="height: auto; position: relative;">
                    <div 
                        v-for="group in groupsWithResources"
                        :key="group.id"
                        :id="group.uniqueId"
                        class="mb-4 bg-gray-200 border rounded-lg overflow-hidden"
                    >
                        <b-button block v-b-toggle="`dochub-group-collapse-${group.id}`" variant="zone-dark" class="px-3 py-3 rounded-0 dochub-group-collapse-button">
                            <div class="d-flex align-items-end justify-content-between">
                                <div class="d-flex align-items-center justify-content-start">
                                    <div class="text-left">
                                        <h3 class="mb-0 text-primary-darker">{{ group.name }}</h3>
                                        <p v-if="group.description" class="text-muted mb-0 mt-2">{{ group.description }}</p>
                                    </div>
                                </div>
                                <div class="align-self-center d-flex gap-4 justify-content-center align-items-end">
                                    <div class="align-self-center font-size-100">{{ group.resources.length }} item{{ group.resources.length > 1 ? 's' : ''}}</div>
                                    <div class="dochub-group-collapse-icons mr-4 ml-2">
                                        <b-icon icon="chevron-down" class="collapse-icon" font-scale="2" variant="secondary"></b-icon>
                                    </div>
                                </div>
                            </div>
                        </b-button>
                        <b-collapse
                            :id="`dochub-group-collapse-${group.id}`"
                            visible
                            class="group-collapse"
                        >
                            <div class="border-top py-3 px-3">  <!-- separate from collapse so we don't have janky collapsing, separate from items-wrapper so we don't interfere with list controlling classes -->
                                <div :class="`group-items-wrapper group-items-${group.layout_type}`">
                                    <div
                                        v-for="resource in group.resources"
                                        :key="resource.id"
                                        class="resource-item"
                                    >  <!-- we wrap in another div so we don't mess up the classes on root of component -->
                                        <DocHubResourceDisplay
                                            :resource="resource"
                                            :edit="false"
                                            buttonSize="md"
                                            :layoutType="group.layout_type"
                                        ></DocHubResourceDisplay>  <!-- set fileDownloadButtonProps in the ResourceDisplay component -->
                                    </div>
                                </div>
                            </div>
                        </b-collapse>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="noDocsAvailable" class="w-100 d-flex justify-content-center align-items-center" style="min-height: 400px; height: 400px;">
            <div class="font-weight-bold font-size-100 text-muted">
                No Documents Available
            </div>
        </div>
    </div>
</template>

<script>
//import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin, splUrlMapMixin } from '../../js/custom-vue-mixins';
import DocHubResourceDisplay from './DocHubResourceDisplay.vue';
import { fetchGroupsWithResources } from './dochubHelpers';


export default {
    name: 'DocHubViewer',
    components: {
        DocHubResourceDisplay,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        splUrlMapMixin,
    ],
    props: {
        hideTitle: {
            type: Boolean,
            default: false,
        },
        onlyPublished: {  // if you want to FORCE to only show PUBLISHED docs, set this -- like for portal preview
            // backend will still never show non-published things to people who should not see them
            // this is only for limiting to only published for people who can otherwise see all
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            initialLoadComplete: false,
            resourcesById: {},  // map of resource_id to resource
            groupsWithResources: [],  // from API 
        }
    },
    computed: {
        noDocsAvailable() {
            return this.initialLoadComplete && Object.values(this.resourcesById).length < 1;
        },
        hasMultipleGroups() {
            return this.groupsWithResources.length > 1;
        },
    },
    mounted() {
        this.getResourcesAndGroups();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async refresh() {
            await this.getResourcesAndGroups();
        },
        async getResourcesAndGroups() {
            this.loading = true;
            try {
                this.groupsWithResources = await fetchGroupsWithResources({forManager: false, onlyPublished: this.onlyPublished});
                // now have to set this.resourcesById
                const allResources = [];
                this.groupsWithResources.forEach(g => {
                    allResources.push(...g.resources);
                });
                this.resourcesById = Object.fromEntries(allResources.map(r => [r.id, r]));
                this.initialLoadComplete = true;
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loading = false;
        },
    }
}
</script>

<style lang="scss">

.dochub-viewer {

    .dochub-group-collapse-button.collapsed .dochub-group-collapse-icons > .when-not-collapsed,
    .dochub-group-collapse-button.not-collapsed .dochub-group-collapse-icons > .when-collapsed {
        display: none;
    }
    .dochub-group-collapse-button {
        overflow: hidden;
        .collapse-icon {
            transition: transform 0.3s;
        }
        &.collapsed {
            background-color: var(--gray-200);
            &:hover {
                background-color: var(--gray-300);
            }
            .not-collapsed-text {
                display: none;
            }
            .collapse-icon {
                transform: rotate(0deg);
            }
        }
        &.not-collapsed {
            background-color: var(--gray-200);
            &:hover {
                background-color: var(--gray-300);
            }
            .collapsed-text {
                display: none;
            }
            .collapse-icon {
                transform: rotate(180deg);
            }
        }
    }

    .group-collapse {

        .group-items-wrapper {
            width: 100%;

            .resource-item {  // common things for items in all layouts
                background-color: #fff; // var(--gray-100);
                overflow: hidden;
                // add shadow to cards?
            }

            &.group-items-SMALL_CARDS {
                display: flex;
                flex-wrap: wrap;
                //justify-content: space-around;
                gap: 2rem;

                .resource-item {
                    padding: 0.75rem 1.25rem;
                    border: 1px solid rgba(0, 0, 0, 0.125);
                    border-radius: 0.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;   // shadow-sm
                    width: calc((100% - 4rem) / 3);  // 3 items per row
                }
            }
            &.group-items-LARGE_CARDS {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 2.5rem;

                .resource-item {
                    padding: 0.75rem 1.25rem;
                    border: 1px solid rgba(0, 0, 0, 0.125);
                    border-radius: 0.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;   // shadow-sm
                    width: 45%;
                }
            }
            &.group-items-FULL_CARDS {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 1rem;

                .resource-item {
                    padding: 0.75rem 1.25rem;
                    border: 1px solid rgba(0, 0, 0, 0.125);
                    border-radius: 0.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;   // shadow-sm
                    width: 100%;
                }
            }
            &.group-items-GRID {
                display: flex;
                flex-wrap: wrap;
                //justify-content: space-around;
                gap: 2rem;

                .resource-item {
                    padding: 0.75rem 1.25rem;
                    border: 1px solid rgba(0, 0, 0, 0.125);
                    border-radius: 0.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;   // shadow-sm
                    width: calc((100% - 4rem) / 3);  // 3 items per row
                }
            }
            &.group-items-LIST {  // .list-group and .list-group-item
                display: flex;
                flex-direction: column;
                border: 1px solid rgba(0, 0, 0, 0.125);
                border-radius: 0.5rem;
                box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;   // shadow-sm
                overflow: hidden;

                .resource-item {
                    position: relative;
                    display: block;
                    padding: 0.75rem 1.25rem;
                    &:not(:last-of-type) {
                        border-bottom: 1px solid rgba(0, 0, 0, 0.125);
                    }
                }
            }
        }
    }
}

</style>